// ─────────────────────────────────────────────
//  useDailyRoster — real-time daily provider list for a facility + date
//
//  The roster lives on the daily_boards/{facilityId_date} doc and names the
//  day's AIC, supervising doctors, and CRNAs. It scopes the OR board's
//  assignment dropdowns and (for today) drives who the active AIC is. A missing
//  doc means "no list yet" — the roster is returned with empty arrays.
// ─────────────────────────────────────────────
import { useEffect, useState } from "react";
import { dayKey } from "../domain/dayKey";
import { listenToDailyBoard } from "../firebase/firestore";

// Day key (YYYY-MM-DD) for the daily roster / assignments / board / breaks —
// the wall-clock "OR day", never UTC. (UTC rolled the board to the next day in
// the evening for US facilities and filed evening edits under tomorrow.) This
// is the single source of truth: every day-scoped doc id and day filter keys
// off it.
//
// ⚠️ THE HOSPITAL'S DAY, NOT THE DEVICE'S. This used to build the key from the
// device's own date while every Cloud Function builds it in America/New_York,
// so a device in another zone addressed DIFFERENT day-scoped documents than the
// server for the same moment — a confirmation the server never reads, or a
// board that looks empty, with no error either way. One definition now, in
// domain/dayKey.js, defaulting to the zone the functions already use.
//
// Identical output for every device in Florida, which is every device today.
export const rosterDateKey = (d = new Date(), tz) => dayKey(d, tz);

// Local-midnight epoch (ms) for a YYYY-MM-DD key — used for "today only"
// filters. Parsing the parts (not Date.parse, which treats a bare YYYY-MM-DD as
// UTC) keeps it on the local day boundary.
//
// ⚠️ STILL THE DEVICE'S MIDNIGHT, deliberately. Making this the FACILITY's
// midnight needs zone-aware epoch arithmetic, and it feeds "since the start of
// today" comparisons where being a few hours out only matters for a device
// outside the facility's zone — the same case `dayKey` now handles, and a much
// smaller consequence than addressing the wrong document. Left explicit rather
// than silently mixed: if a facility is ever configured with a `timeZone` that
// is not the device's, this is the next thing to convert.
export const startOfDayMs = (dateKey) => {
  const [y, m, d] = String(dateKey || "").split("-").map(Number);
  if (!y || !m || !d) return 0;
  return new Date(y, m - 1, d).getTime();
};

const EMPTY_ROSTER = { aicId: null, aicName: "", supervisors: [], crnas: [], departed: {}, givingBreaks: {}, crnaCallList: [], mdCallList: [], mdCallHome: [], locumLeaveTimes: {}, hiddenRooms: [] };

export function useDailyRoster(facilityId, date) {
  const [roster, setRoster]   = useState(EMPTY_ROSTER);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!facilityId || !date) return;
    setLoading(true);
    const unsub = listenToDailyBoard(facilityId, date, (snap) => {
      const data = snap.exists() ? snap.data() : null;
      setRoster({
        aicId:       data?.aicId ?? null,
        aicName:     data?.aicName ?? "",
        supervisors: Array.isArray(data?.supervisors) ? data.supervisors : [],
        crnas:       Array.isArray(data?.crnas) ? data.crnas : [],
        // Phase 4 (D-06): memberId → boolean map of who has departed today.
        departed:      data?.departed && typeof data.departed === "object" ? data.departed : {},
        // memberId → boolean map of who is walking the break rounds. Same shape
        // as `departed`, and it is here for the reason the mdCallHome comment
        // below spells out: the write landed in Firestore and the chip never
        // appeared, because this mapper drops any field it does not name.
        givingBreaks:  data?.givingBreaks && typeof data.givingBreaks === "object" ? data.givingBreaks : {},
        // CR-01 fix: carry call-list arrays so persisted slots round-trip.
        crnaCallList:  Array.isArray(data?.crnaCallList) ? data.crnaCallList : [],
        mdCallList:    Array.isArray(data?.mdCallList) ? data.mdCallList : [],
        // TV X-button removals pending Home confirmation (facility-wide sync).
        // This hook SHAPES the roster — an unmapped field silently vanishes
        // downstream (live-found: the removal wrote mdCallHome but the Home
        // box stayed empty until this mapping existed).
        mdCallHome:    Array.isArray(data?.mdCallHome) ? data.mdCallHome : [],
        // Locked locum leaving times ({ CODE: "HH:MM" }) — Timeline Locum List.
        locumLeaveTimes: data?.locumLeaveTimes && typeof data.locumLeaveTimes === "object" ? data.locumLeaveTimes : {},
        // Gap 5b: rooms hidden by Remove on doc-less template rows.
        hiddenRooms:   Array.isArray(data?.hiddenRooms) ? data.hiddenRooms : [],
      });
      setLoading(false);
    });
    return unsub;
  }, [facilityId, date]);

  return { roster, loading };
}
